import {
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Property } from '../ProjectConfig/types';
import { PropertyType } from '../ProjectConfig/enums';
import { PRIMARY, RED } from '../../Style/GeoMobyBaseTheme';
import { useState } from 'react';
import { VOID } from '../../util/typeUtils';

export const SelectValue = ({
  property,
  allProperties,
  isFromTable,
  adornment,
  onKeyPress,
  onChangeValue,
  onBlurValue,
}: {
  property: { index: number; property: string; value: string };
  allProperties: Property[];
  isFromTable?: boolean;
  adornment?: string;
  onKeyPress: (event: { key: string }) => void;
  onChangeValue: (value: string, prop: { index: number; property: string; value: string }) => void;
  onBlurValue: (
    value: string,
    prop: { index: number; property: string; value: string },
  ) => Promise<void>;
}) => {
  const [propertyValue, setPropertyValue] = useState<string>(property.value);
  const existingProperty = allProperties.find(p => p.label === property.property);
  const isListProperty = existingProperty?.type === PropertyType.List;
  const isNumberProperty = existingProperty?.type === PropertyType.Number;
  const isInvalidNumberValue = isNumberProperty && isNaN(Number(property.value));
  const isInvalidListValue =
    isListProperty && !existingProperty?.items?.find(item => item === property.value);
  const isInvalidProperty = !existingProperty && !!property.property;
  const isOptionalValue = property.property === 'emissions_rate';

  return (
    <FormControl
      style={{
        width: '100%',
      }}
    >
      {isInvalidProperty ? (
        <TextField
          id={`${property.index}-invalid-value-cell`}
          key={`${property.index}-invalid-value-cell`}
          name={`${property.index}-invalid-value-cell`}
          label={isFromTable ? '' : 'Value'}
          value={propertyValue}
          sx={{
            '& .MuiTextField-root': {
              height: '60px',
            },
          }}
          onChange={() => VOID}
          onKeyPress={event => onKeyPress(event)}
          InputLabelProps={{ shrink: true }}
          helperText={
            isFromTable ? '' : 'INVALID. Either delete here or create in Project Configuration.'
          }
          error={true}
        />
      ) : (
        <>
          {/* Number Value */}
          {existingProperty?.type === PropertyType.Number && (
            <TextField
              id={`${property.property}-number-value-cell`}
              label={isFromTable ? '' : 'Value'}
              key={`${property.property}-number-value-cell`}
              type="number"
              name={`${property.property}-number-value-cell`}
              value={propertyValue}
              sx={{
                '& p': {
                  color: PRIMARY,
                },
                // This is applied after page refresh.
                '& .MuiTextField-root': {
                  height: '60px',
                },
                // This is applied on start up. Really not sure why, but setting these to heights seems to fix the problem we see on start-up, where the input box is squashed.
                // These are the only fields that I've seen this happen to.
                '& .MuiInputBase-root': {
                  height: '57px',
                },
              }}
              onChange={e => {
                onChangeValue(e.target.value, property);
                setPropertyValue(e.target.value);
              }}
              onBlur={e => onBlurValue(e.target.value, property)}
              onKeyPress={event => {
                if (event.key !== 'Enter') return;
                onBlurValue(propertyValue, property);
                onKeyPress(event);
              }}
              InputProps={
                adornment
                  ? {
                      endAdornment: (
                        <InputAdornment position="end">
                          <strong>{adornment}</strong>
                        </InputAdornment>
                      ),
                    }
                  : undefined
              }
              InputLabelProps={{ shrink: true }}
              placeholder={'123'}
              helperText={propertyValue || isOptionalValue ? '' : 'Required'}
              error={isInvalidNumberValue}
            />
          )}

          {/* String Value */}
          {existingProperty?.type === PropertyType.String && (
            <TextField
              id={`${property.property}-string-value-cell`}
              label={isFromTable ? '' : 'Value'}
              key={`${property.property}-string-value-cell`}
              type={'string'}
              name={`${property.property}-string-value-cell`}
              sx={{
                '& p': {
                  color: PRIMARY,
                },
                // Ditto (above comments).
                '& .MuiTextField-root': {
                  height: '60px',
                },
                '& .MuiInputBase-root': {
                  height: '57px',
                },
              }}
              value={propertyValue}
              onChange={(e: { target: { value: string } }) => {
                onChangeValue(e.target.value, property);
                setPropertyValue(e.target.value);
              }}
              onBlur={(e: { target: { value: string } }) => onBlurValue(e.target.value, property)}
              onKeyPress={(event: { key: string }) => {
                if (event.key !== 'Enter') return;
                onBlurValue(propertyValue, property);
                onKeyPress(event);
              }}
              InputLabelProps={{ shrink: true }}
              placeholder={'abc'}
              helperText={propertyValue || isOptionalValue ? '' : 'Required'}
            />
          )}

          {/* List Value */}
          {existingProperty?.type === PropertyType.List && (
            <>
              <InputLabel
                id="select-value"
                sx={{
                  color: isInvalidListValue ? RED : 'auto',
                }}
              >
                {isFromTable ? '' : 'Value'}
              </InputLabel>
              <Select
                fullWidth
                id={`${property.property}-string-list-cell`}
                key={`${property.property}-string-list-cell`}
                name={`${property.property}-string-list-cell`}
                label={isFromTable ? '' : 'Value'}
                sx={{
                  '& .MuiTextField-root': {
                    height: '60px',
                  },
                }}
                value={propertyValue !== '' ? propertyValue : existingProperty?.items?.[0]}
                onChange={async (e: { target: { value: string } }) => {
                  onBlurValue(e.target.value, property);
                  setPropertyValue(e.target.value);
                }}
                error={isInvalidListValue}
              >
                {allProperties
                  .find(p => p.label === property.property)
                  ?.items?.map(item => (
                    <MenuItem key={item} value={item}>
                      <Tooltip title={item}>
                        <Typography
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            width: 'calc(100% - 50px)',
                          }}
                        >
                          {item}
                        </Typography>
                      </Tooltip>
                    </MenuItem>
                  ))}
              </Select>
            </>
          )}
        </>
      )}
      {!isFromTable && (isInvalidListValue || isInvalidNumberValue) && (
        <FormHelperText
          sx={{
            color: RED,
          }}
        >
          INVALID VALUE. Please update.
        </FormHelperText>
      )}
    </FormControl>
  );
};
